/**
 * @file DocumentationPage.jsx
 * @description The central hub for platform documentation, policies, and guidance.
 *              Uses a clean Bento-grid layout synchronized with the main platform theme.
 * @module components/DocumentationPage
 * @requires useAuth - Authentication context for navigation filtering.
 * @requires Navbar - Global navigation.
 * @requires Footer - Global footer.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import { 
    FiArrowLeft, FiShield, FiFileText, FiArrowRight, FiCode, 
    FiBookOpen, FiTerminal, FiLayout, FiHelpCircle, FiArrowUpRight 
} from 'react-icons/fi';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component DocumentationPage
 * @description Main view for listing platform documentation and helpful resources.
 *              Provides links to privacy policies, terms of service, and other
 *              core platform documentation using a responsive Bento grid layout.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered Documentation page.
 */
const DocumentationPage = ({ navigate }) => {
    const { isAuthenticated } = useAuth();
    // ── Component State ──
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "SimpleCode | Documentation";
        // Simulate a brief load for theme consistency
        const timer = setTimeout(() => setLoading(false), 300);
        return () => {
            clearTimeout(timer);
            document.title = "SimpleCode";
        };
    }, []);

    /* ══════════════════════════════════════════════════════════════
     * DATA & CONFIG
     * ══════════════════════════════════════════════════════════════ */

    // ── Navigation Links (Conditional) ──
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    /**
     * @constant docCategories
     * @description Documentation categories mapped to Bento grid cards.
     * @type {Array<{id: string, title: string, description: string, icon: JSX.Element, bgIcon: JSX.Element, action: Function, span: string, color: string}>}
     */
    const docCategories = [
        {
            id: 'privacy',
            title: 'Privacy Policy',
            description: 'How we handle and protect your data. Your privacy is our top priority.',
            icon: <FiShield className="w-6 h-6" />,
            bgIcon: <FiShield className="w-20 h-20" />,
            action: () => navigate('/privacy-policy'),
            span: 'md:col-span-1',
            color: 'text-emerald-600'
        },
        {
            id: 'terms',
            title: 'Terms of Service',
            description: 'The standard agreement for using our platform and services.',
            icon: <FiFileText className="w-6 h-6" />,
            bgIcon: <FiFileText className="w-20 h-20" />,
            action: () => navigate('/terms-of-service'),
            span: 'md:col-span-1',
            color: 'text-emerald-600'
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-accent flex flex-col font-sans">
                <Navbar navigate={navigate} navLinks={navLinks} />
                <main className="flex-1 pt-16">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 animate-pulse mt-8">
                        <div className="h-10 w-64 bg-emerald-100 rounded mb-8" />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...Array(2)].map((_, i) => (
                                <div key={i} className="bg-white rounded p-6 h-48 shadow-sm">
                                    <div className="h-6 w-32 bg-gray-100 rounded mb-4" />
                                    <div className="h-4 w-full bg-gray-50 rounded mb-2" />
                                    <div className="h-4 w-2/3 bg-gray-50 rounded" />
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 pt-16">
                
                {/* ── Documentation Header ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mb-8 mt-4 border-b border-emerald-100/30">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h1 className="text-left text-3xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8">
                                Platform <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Documentation</span>
                            </h1>
                        </div>
                    </div>
                </section>

                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-20">
                    
                    {/* ════════ BENTO GRID OF DOCS ════════ */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {docCategories.map((doc) => (
                            <div 
                                key={doc.id}
                                onClick={doc.action}
                                className={`${doc.span} group bg-white border-l-4 border-emerald-600 rounded p-6 transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col justify-between min-h-[160px]`}
                            >
                                {/* Background Deco Pattern */}
                                <div className="absolute right-[1px] bottom-[2px] opacity-[0.2] transform transition-opacity duration-300 pointer-events-none text-muted-foreground">
                                    {doc.bgIcon}
                                </div>

                                <div className="relative z-10">
                                    <div className={`flex items-center gap-3 mb-4 ${doc.color}`}>
                                        <h3 className="text-lg font-bold text-foreground group-hover:text-emerald-700 transition-colors uppercase tracking-wider">{doc.title}</h3>
                                    </div>
                                    <p className="text-muted-foreground text-sm leading-relaxed max-w-xl text-left">
                                        {doc.description}
                                    </p>
                                </div>

                                <div className="relative z-10 flex items-center gap-1.5 text-xs font-bold text-emerald-600 mt-6 group-hover:gap-2.5 transition-all">
                                    READ MORE <FiArrowRight className="w-4 h-4" />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Divider Decoration
                    <div className="mt-16 flex items-center justify-center gap-4 opacity-20">
                        <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-emerald-600"></div>
                        <FiCode className="w-6 h-6 text-emerald-600" />
                        <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-emerald-600"></div>
                    </div> */}
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

export default DocumentationPage;
